import { DatePipe } from '@angular/common';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { gsActionsColumn } from '../../shared/ui/table/gs-table.columns';
import { Discount } from './models/discount.model';

export function buildDiscountColumns(
  datePipe: DatePipe,
  onToggleEnabled: (discount: Discount) => void,
  onEdit: (discount: Discount) => void,
  onDelete: (discount: Discount) => void,
  canEdit = true
): ColumnDef<Discount>[] {
  const codeColumn: ColumnDef<Discount> = {
    id: 'code',
    header: 'Code',
    accessorFn: discount => discount.code,
  };

  const descriptionColumn: ColumnDef<Discount> = {
    id: 'description',
    header: 'Description',
    accessorFn: discount => discount.description,
  };

  const discountColumn: ColumnDef<Discount> = {
    id: 'discount',
    header: 'Discount',
    accessorFn: discount => `${discount.discountPercentage}%`,
  };

  const usesColumn: ColumnDef<Discount> = {
    id: 'uses',
    header: 'Uses',
    accessorFn: discount =>
      `${discount.activeUsesCount ?? 0} / ${discount.maxUses === null ? '∞' : discount.maxUses}`,
  };

  const validFromColumn: ColumnDef<Discount> = {
    id: 'validFrom',
    header: 'Valid From',
    accessorFn: discount =>
      discount.validFrom
        ? (datePipe.transform(discount.validFrom, 'short') ?? '—')
        : '—',
  };

  const validUntilColumn: ColumnDef<Discount> = {
    id: 'validUntil',
    header: 'Valid Until',
    accessorFn: discount =>
      discount.validUntil
        ? (datePipe.transform(discount.validUntil, 'short') ?? '—')
        : '—',
  };

  const actionsColumn = gsActionsColumn<Discount>({
    actions: [
      {
        id: 'toggle',
        label: discount => (discount.enabled ? 'On' : 'Off'),
        tone: discount => (discount.enabled ? 'positive' : 'negative'),
        dataCy: discount => `discount-enable-button-${discount.id}`,
      },
      {
        id: 'edit',
        label: 'Edit',
        dataCy: discount => `discount-edit-button-${discount.id}`,
      },
      {
        id: 'delete',
        label: 'Delete',
        destructive: true,
        dataCy: discount => `discount-delete-button-${discount.id}`,
      },
    ],
    onSelect: (actionId, discount) => {
      if (actionId === 'toggle') {
        onToggleEnabled(discount);
      } else if (actionId === 'edit') {
        onEdit(discount);
      } else if (actionId === 'delete') {
        onDelete(discount);
      }
    },
    rowDataCy: discount => `discount-row-${discount.id}`,
  });

  return [
    codeColumn,
    descriptionColumn,
    discountColumn,
    usesColumn,
    validFromColumn,
    validUntilColumn,
    ...(canEdit ? [actionsColumn] : []),
  ];
}
